<template>
  <div class="modal-card">
    <section class="modal-card-body" :class="{'loaded': ! isLoading}">
      <hero-bar v-if="plan" class="mb-10">
        {{ plan.name }} - {{ $t(plan.type) }} - {{ plan.time }}
        <h1 slot="right">
          {{ readableDate }}
        </h1>
      </hero-bar>
      <div v-if="plan">
        <card-component class="mb-10">
          <div class="totalRegisterCount">
            Toplam Kişi: <span class="tag">{{ registers.length }}</span>
          </div>
          <div class="columns no-wrap">
            <div class="column one">
              <b-tabs
                :animated="false"
                :destroy-on-hide="true"
                size="is-medium"
                type="is-boxed"
              >
                <b-tab-item label="Liste" icon="table-large">
                  <b-table
                    checkbox-position="right"
                    :data="registers"
                    :checked-rows.sync="checkedRows"
                    checkable
                    striped
                    sticky-header
                    hoverable
                    class="no-wrap"
                  >
                    <template slot-scope="props">
                      <b-table-column custom-key="actions" width="90" class="is-actions-cell">
                        <b-tooltip :label="$t('cancelRequest')" type="is-dark" position="is-right">
                          <b-button outlined size="is-small" class="button-green is-danger mr-5 only-icon" icon-left="close-circle"
                                    @click="cancelRequest(props.row.id, false)"
                          />
                        </b-tooltip>
                        <b-button
                          outlined
                          size="is-small"
                          class="button-purble btn-details"
                          icon-left="arrow-right-bold"
                          @click="$parent.$parent.registerDetails(props.row.id)"
                        />
                      </b-table-column>

                      <b-table-column :label="$t('fullName')" field="user.name">
                        <b-tooltip
                          :label="props.row.user.name"
                          type="is-dark"
                          position="is-bottom"
                        >
                          {{ props.row.user.name | limitShow }}
                        </b-tooltip>
                      </b-table-column>

                      <b-table-column :label="$t('phone')" field="phone">
                        <a :href="`tel:${props.row.user.phone}`">{{ props.row.user.phone | phone }}</a>
                      </b-table-column>

                      <b-table-column :label="$t('address')" field="address">
                        <b-tooltip
                          :label="props.row.address"
                          type="is-dark"
                          position="is-bottom"
                        >
                          {{ props.row.address | formatAddress }}
                        </b-tooltip>
                      </b-table-column>

                      <b-table-column :label="$t('driver')">
                        <span v-if="props.row.driver">
                          <b-tooltip
                            :label="props.row.driver.name"
                            type="is-dark"
                            position="is-bottom"
                          >
                            {{ props.row.driver.name | limitShow }}
                          </b-tooltip>
                        </span>
                      </b-table-column>

                      <b-table-column :label="$t('carTxt')">
                        <span v-if="props.row.car" class="tag">{{ props.row.car.plate }}</span>
                      </b-table-column>
                    </template>
                    <template slot="bottom-left">
                      <b>{{ $t('selected') }}</b>: {{ checkedRows.length }}
                    </template>
                  </b-table>
                </b-tab-item>

                <b-tab-item label="Harita" icon="google-maps">
                  <LMap
                    ref="map"
                    :center="shuttleCenterLocation"
                    :zoom="mapZoom"
                    class="map"
                    @update:zoom="updateZoom"
                  >
                    <LTileLayerGoogleMutant />
                    <LMarker
                      :lat-lng="coordinateToArray(plan.start_location)"
                      :icon="markers.plan"
                    >
                      <LTooltip>{{ plan.name }}</LTooltip>
                    </LMarker>
                    <MarkerCluster
                      ref="mcluster"
                      @ready="readyMarkerCluster"
                    >
                      <LMarker
                        v-for="(register, index) in registers"
                        :key="`marker-${index}`"
                        :lat-lng="coordinateToArray(register.location)"
                        :icon="markerIcon(register)"
                        @click="markerClick(register)"
                      >
                        <LTooltip>
                          {{ register.user.name }}
                          <span v-if="register.driver">
                            <br> {{ register.driver.name }}
                            <span v-if="register.car"> / {{ register.car.plate }} </span>
                          </span>
                        </LTooltip>
                      </LMarker>
                    </MarkerCluster>
                  </LMap>

                  <b-notification :closable="false" class="mt-10">
                    <b>{{ $t('selected') }}</b>: {{ checkedRows.length }}
                  </b-notification>
                </b-tab-item>
              </b-tabs>
            </div>

            <div
              v-if="checkPermission('shuttle_set_driver')"
              class="column two"
            >
              <div class="border-select">
                <b-field :label="$t('driver')">
                  <b-autocomplete
                    v-model="fakeDriver"
                    field="attributes.name"
                    :data="drivers"
                    :placeholder="$t('chooseDriver')"
                    :loading="isDriverFetching"
                    @typing="getAsyncDriverData"
                    @select="selectDriver"
                  >
                    <template slot-scope="props">
                      <div class="media">
                        <div class="media-content">
                          <b>{{ props.option.attributes.name }}</b>
                          <br>
                          <small>
                            {{ props.option.attributes.phone | phone }}
                          </small>
                        </div>
                      </div>
                    </template>
                  </b-autocomplete>
                </b-field>

                <b-field :label="$t('carTxt')">
                  <b-autocomplete
                    v-model="fakeCarPlate"
                    field="plate"
                    :data="cars"
                    :placeholder="$t('chooseCar')"
                    :loading="isCarFetching"
                    @typing="getAsyncCarData"
                    @select="selectCar"
                  >
                    <template slot-scope="props">
                      <span>
                        {{ props.option.plate }}
                        <span class="emptyText">({{ props.option.brand }} - {{ props.option.model }})</span>
                      </span>
                    </template>
                  </b-autocomplete>
                </b-field>

                <b-field>
                  <b-button type="is-purple" class="is-fullwidth"
                            :class="{'is-loading': setIsLoading}"
                            :disabled="saveButtonDisable"
                            @click="setDriverAndCar"
                  >
                    {{ $t('save') }}
                  </b-button>
                </b-field>
              </div>
            </div>
          </div>
        </card-component>
      </div>
    </section>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import moment from 'moment';
import L from 'leaflet';
import { LMap, LMarker, LTooltip } from 'vue2-leaflet'; // https://vue2-leaflet.netlify.app/components/LMap.html#demo

export default {
  name: 'ShuttleDayDetails',
  components: {
    HeroBar: () => import('./HeroBar'),
    CardComponent: () => import('./CardComponent'),
    LTileLayerGoogleMutant: () => import('vue2-leaflet-googlemutant'), // https://github.com/jperelli/vue2-leaflet-googlemutant
    MarkerCluster: () => import('vue2-leaflet-markercluster'), // https://github.com/jperelli/vue2-leaflet-markercluster
    LMap,
    LMarker,
    LTooltip,
  },
  props: {
    shuttleId: {
      type: Number,
      default: null
    },
    registerDate: {
      type: String,
      default: null
    },
  },
  data () {
    return {
      registers: null,
      plan: null,
      isLoading: true,
      setIsLoading: false,
      checkedRows: [],
      isDriverFetching: false,
      isCarFetching: false,
      drivers: [],
      cars: [],
      driverId: null,
      carId: null,
      fakeDriver: '',
      fakeCarPlate: '',
      mapZoom: 12,
      markers: {
        plan: L.icon({
          iconUrl: '/img/markers/plan.png',
          iconSize: [48, 48],
          iconAnchor: [48 / 2, 48],
          tooltipAnchor: [16, -24],
        }),
        okey: L.icon({
          iconUrl: '/img/markers/okey.png',
          iconSize: [38, 38],
          iconAnchor: [28 / 2, 28],
          tooltipAnchor: [16, -16],
        }),
        normal: L.icon({
          iconUrl: '/img/markers/normal.png',
          iconSize: [38, 38],
          iconAnchor: [28 / 2, 28],
          tooltipAnchor: [16, -16],
        }),
        selected: L.icon({
          iconUrl: '/img/markers/selected.png',
          iconSize: [38, 38],
          iconAnchor: [28 / 2, 28],
          tooltipAnchor: [16, -16],
        })
      },
    }
  },
  computed: {
    readableDate () {
      return moment(this.registerDate).locale(this.$i18n.locale).format('DD MMMM YYYY dddd')
    },
    saveButtonDisable () {
      let returnB = true;
      if ((this.driverId || this.carId) && this.checkedRows.length > 0) {
        returnB = false;
      }
      return returnB;
    },
    shuttleCenterLocation () {
      return this.coordinateToArray(this.plan.start_location);
    },
    // mapZoom () {
    //   return this.$refs.map.mapObject.getZoom()
    // }
  },
  watch: {
    fakeDriver (n) {
      if (n == '') {
        this.driverId = null;
      }
    },
    fakeCarPlate (n) {
      if (n == '') {
        this.carId = null;
      }
    }
  },
  mounted () {
    this.showSpinner()
    this.getDetails()
  },
  methods: {
    getDetails () {
      const params = {
        shuttle_id: this.shuttleId,
        register_date: moment(this.registerDate).format('YYYY-MM-DD'),
      };

      this.$http.get('shuttle-day-details', { params })
        .then(({ data }) => {
          this.plan = data.plan;
          this.registers = data.registers;
          this.isLoading = false;

          this.carId = null
          this.driverId = null
          this.fakeDriver = ''
          this.fakeCarPlate = ''
          this.checkedRows = []
          this.setIsLoading = false;
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: this.$t('error'),
            type: 'is-warning',
            duration: 5000
          });
          this.$parent.close();
        })
        .finally(() => {
          this.hideSpinner()
        });
    },
    readyMarkerCluster () {
      const mapBounds = this.$refs.mcluster.mapObject.getBounds()
      this.$refs.map.mapObject.fitBounds(mapBounds, { maxZoom: this.mapZoom });
    },
    setDriverAndCar () {
      const ids = this.$_.transform(this.checkedRows, (result, value) => {
        result.push(value.id);
      });

      const params = {
        ids: ids,
        driver_id: this.driverId,
        car_id: this.carId,
      };

      this.setIsLoading = true;

      this.$http.put('shuttle-register-update', params)
      .then(({ data }) => {
        if (data.success) {
          this.getDetails();

          this.$buefy.toast.open({
            message: 'Başarıyla Güncellendi',
            type: 'is-success',
            duration: 2000
          });
        }
      })
      .catch(() => {
          this.setIsLoading = false;
          this.$buefy.toast.open({
            message: this.$t('error'),
            type: 'is-warning',
            duration: 5000
          });
        })
    },
    getAsyncDriverData: debounce(function (name) {
      this.drivers = []
      if (!name.length) {
        return
      }
      this.isDriverFetching = true
      this.$http.get(`list-users?role=4&name=${name}`)
          .then(({ data }) => {
            this.drivers = data.data;
          })
          .catch(() => {
            this.drivers = []
          })
          .finally(() => {
            this.isDriverFetching = false
          })
    }, 500),
    getAsyncCarData: debounce(function (name) {
      this.cars = []
      if (!name.length) {
        return
      }
      this.isCarFetching = true
      this.$http.get(`list-cars?q=${name}`)
          .then(({ data }) => {
            this.cars = data
          })
          .catch(() => {
            this.cars = []
          })
          .finally(() => {
              this.isCarFetching = false
          })
    }, 500),
    selectDriver (option) {
      if (option) {
        this.driverId = option.id;
        this.drivers = [];
      }
    },
    selectCar (option) {
      if (option) {
        this.carId = option.id;
        // this.fakeCarPlate = option.plate;
        this.cars = [];
      }
    },
    cancelRequest (id) {
      this.$buefy.dialog.confirm({
          message: 'Emin misiniz?',
          cancelText: this.$t('no'),
          confirmText: this.$t('yes'),
          type: 'is-danger',
          onConfirm: () => {
            this.$http
              .delete('shuttle-register-delete', {
                data: {
                  id: id,
                  all: false
                }
              })
              .then(() => {
                this.$buefy.toast.open({message: this.$t('success'), type: 'is-success'})
                this.getDetails();
              })
          }
      })
    },
    markerClick (register) {
      if (this.$_.includes(this.checkedRows, register)) {
        this.checkedRows = this.$_.reject(this.checkedRows, {id: register.id});
      } else {
        this.checkedRows.push(register);
      }
    },
    markerIcon (register) {
      if (this.$_.includes(this.checkedRows, register)) {
        return this.markers.selected
      } else {
        if (register.driver) {
          return this.markers.okey
        }

        return this.markers.normal
      }
    },
    updateZoom (zoom) {
      this.mapZoom = zoom;
    }
  }
}
</script>

<style lang="scss" scoped>
  .modal-card {
    width: 90vw !important;

    .modal-card-body {
      background-color: #f8f8f8;
      transform: scaleY(0);
      transform-origin: top;
      transition: transform 0.26s ease;

      .column.one {
        max-width: 70%;
        margin-top: 20px;
      }
      .column.two {
        max-width: 30%;
        margin-top: 90px;
      }
      h1 {
        font-size: 25px;
        font-weight: bold;
      }
      .totalRegisterCount {
        font-size: 18px;
        font-weight: bold;
      }
      .border-select {
        position: relative;
        display: flex;
        flex-direction: column;
        border: 2px solid #f5f5f5;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
        border-bottom-left-radius: 4px;
        color: rgba(0,0,0,.7);
        padding: .5rem;

        &:before {
          background: #ffdd57;
          border-radius: 4px 4px 0 0;
          bottom: 100%;
          content: "Seçili Kullanıcılar İçin Ata";
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          left: -1px;
          padding: 4px 8px;
          position: absolute;
          text-transform: uppercase;
          vertical-align: top;
        }
      }
    }
  }

  .map {
    height: calc(100vh - 460px) !important;
  }
</style>

<style>
  .modal-card .b-table .has-sticky-header {
    height: calc(100vh - 445px) !important;
  }

  .leaflet-tooltip-pane {
    font-size: 14px;
  }

  .leaflet-tooltip {
    opacity: 1 !important;
  }
</style>
